/**
 * HologramStage — cinematic, investor-grade stage wrapper around the
 * existing PerformingHologram. Adds:
 *
 *   1. Volumetric GOD-RAY canvas behind the performer (radial beams of light
 *      streaming from the artist's silhouette, beat-pulsing).
 *   2. Floor REFLECTION pool — a mirrored, blurred, dim copy of the
 *      performer flooded onto a glowing oval of light.
 *   3. SPARK / DUST particle field in front (200 motes drifting in the rays,
 *      kicked outward on every bass hit).
 *   4. ATMOSPHERIC HAZE + scrolling hex-grid interference overlay.
 *   5. RIM BLOOM via stacked drop-shadow + screen blend.
 *
 * Pure CSS / Canvas2D / WebGL — zero AI credits, zero new APIs.
 *
 * Drop-in replacement for <PerformingHologram>.
 */
import { useEffect, useRef } from "react";

/* ---------------- GOD RAY CANVAS ---------------- */
function GodRays({ analyser, bpm = 108 }) {
  const ref = useRef(null);
  const raf = useRef(0);
  const bpmRef = useRef(bpm);
  useEffect(() => { bpmRef.current = bpm > 0 ? bpm : 108; }, [bpm]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(r.width * dpr));
      canvas.height = Math.max(1, Math.floor(r.height * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const freq = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const start = performance.now();

    // Pre-generate ray angles so they don't randomly jitter every frame
    const RAY_COUNT = 24;
    const rays = Array.from({ length: RAY_COUNT }, (_, i) => ({
      a: (i / RAY_COUNT) * Math.PI * 2 + Math.random() * 0.05,
      w: 0.04 + Math.random() * 0.07,
      phase: Math.random() * Math.PI * 2,
    }));

    const loop = (now) => {
      const t = (now - start) / 1000;
      let bass = 0, energy = 0;
      if (analyser && freq) {
        analyser.getByteFrequencyData(freq);
        const n = freq.length;
        const bEnd = Math.floor(n * 0.12);
        let bs = 0, total = 0;
        for (let i = 0; i < bEnd; i++) bs += freq[i];
        for (let i = 0; i < n; i++) total += freq[i];
        bass = bs / (bEnd || 1) / 255;
        energy = total / (n * 255);
      } else {
        const liveBpm = Math.max(40, Math.min(220, bpmRef.current || 108));
        const beatLen = 60 / liveBpm;
        const beatPos = (t % beatLen) / beatLen;
        bass = Math.exp(-beatPos * 8) * 0.95 + 0.15;
        energy = 0.45 + 0.18 * Math.sin(t * 0.8);
      }

      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // Radial deep-blue/cyan halo behind performer
      const cx = w * 0.5;
      const cy = h * 0.55;
      const haloR = Math.min(w, h) * (0.42 + bass * 0.08);
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, haloR);
      grad.addColorStop(0, `rgba(120, 230, 255, ${0.22 + bass * 0.18})`);
      grad.addColorStop(0.35, `rgba(30, 180, 255, ${0.12 + bass * 0.1})`);
      grad.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Volumetric god rays — long, slow-rotating cones
      ctx.save();
      ctx.translate(cx, cy);
      ctx.globalCompositeOperation = "screen";
      const rayLen = Math.max(w, h) * 1.2;
      for (const r of rays) {
        const swing = Math.sin(t * 0.18 + r.phase) * 0.04;
        const ang = r.a + t * 0.018 + swing;
        const intensity = 0.06 + 0.16 * (0.5 + 0.5 * Math.sin(t * 0.7 + r.phase)) + bass * 0.18;
        ctx.save();
        ctx.rotate(ang);
        const g = ctx.createLinearGradient(0, 0, rayLen, 0);
        g.addColorStop(0, `rgba(180, 240, 255, ${intensity})`);
        g.addColorStop(0.6, `rgba(120, 200, 255, ${intensity * 0.35})`);
        g.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(rayLen, -rayLen * r.w);
        ctx.lineTo(rayLen, rayLen * r.w);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();

      // Subtle pink rim flare from below
      const flare = ctx.createRadialGradient(cx, h, 0, cx, h, h * 0.7);
      flare.addColorStop(0, `rgba(255, 60, 160, ${0.28 + energy * 0.18})`);
      flare.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.globalCompositeOperation = "screen";
      ctx.fillStyle = flare;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "source-over";

      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf.current); ro.disconnect(); };
  }, [analyser]);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" style={{ mixBlendMode: "screen" }} data-testid="hologram-godrays" />;
}

/* ---------------- DUST / SPARK FIELD ---------------- */
function DustField({ analyser, bpm = 108 }) {
  const ref = useRef(null);
  const raf = useRef(0);
  const bpmRef = useRef(bpm);
  useEffect(() => { bpmRef.current = bpm > 0 ? bpm : 108; }, [bpm]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 1, H = 1;
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = W = Math.max(1, Math.floor(r.width * dpr));
      canvas.height = H = Math.max(1, Math.floor(r.height * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Two layers: slow dust motes + fast sparks from artist core
    const DUST = Array.from({ length: 110 }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00015,
      vy: -0.00025 - Math.random() * 0.0004,
      r: 0.6 + Math.random() * 1.4,
      hue: 180 + Math.random() * 60, // cyan→blue
      phase: Math.random() * Math.PI * 2,
    }));
    const sparks = []; // emitted on beat

    const freq = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const start = performance.now();
    let lastBass = 0, lastBeatT = 0;

    const loop = (now) => {
      const t = (now - start) / 1000;
      let bass = 0, treb = 0;
      if (analyser && freq) {
        analyser.getByteFrequencyData(freq);
        const n = freq.length;
        const bEnd = Math.floor(n * 0.12);
        const tStart = Math.floor(n * 0.55);
        let bs = 0, ts = 0;
        for (let i = 0; i < bEnd; i++) bs += freq[i];
        for (let i = tStart; i < n; i++) ts += freq[i];
        bass = bs / (bEnd || 1) / 255;
        treb = ts / (n - tStart || 1) / 255;
      } else {
        const liveBpm = Math.max(40, Math.min(220, bpmRef.current || 108));
        const beatLen = 60 / liveBpm;
        const beatPos = (t % beatLen) / beatLen;
        bass = Math.exp(-beatPos * 8) * 0.9 + 0.1;
        const hatPos = ((t * 2) % beatLen) / beatLen;
        treb = Math.exp(-hatPos * 14) * 0.55 + 0.1;
      }

      // Beat detection — emit a burst of sparks from the artist's chest
      const jump = bass - lastBass;
      lastBass = lastBass * 0.92 + bass * 0.08;
      if (jump > 0.18 && t - lastBeatT > 0.28) {
        lastBeatT = t;
        for (let i = 0; i < 28; i++) {
          const ang = Math.random() * Math.PI * 2;
          const speed = 0.0025 + Math.random() * 0.006;
          sparks.push({
            x: 0.5 + (Math.random() - 0.5) * 0.04,
            y: 0.55 + (Math.random() - 0.5) * 0.05,
            vx: Math.cos(ang) * speed,
            vy: Math.sin(ang) * speed - 0.001,
            life: 1,
            hue: 180 + Math.random() * 80,
          });
        }
      }

      // Trail/fade previous frame — use destination-out so old pixels fade
      // their ALPHA to zero instead of building up an opaque black layer
      // which would otherwise obscure the performer behind this canvas.
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.10)";
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "screen";

      // Dust motes
      for (const d of DUST) {
        d.x += d.vx;
        d.y += d.vy;
        if (d.y < -0.05) { d.y = 1.05; d.x = Math.random(); }
        if (d.x < -0.05) d.x = 1.05;
        if (d.x > 1.05) d.x = -0.05;
        const sway = Math.sin(t * 0.6 + d.phase) * 0.005;
        const px = (d.x + sway) * W;
        const py = d.y * H;
        const twinkle = 0.45 + 0.55 * Math.sin(t * 1.4 + d.phase);
        const a = (0.18 + twinkle * 0.45) * (0.75 + treb * 0.5);
        ctx.fillStyle = `hsla(${d.hue}, 90%, 75%, ${a})`;
        ctx.beginPath();
        ctx.arc(px, py, d.r * dpr * (1 + treb * 0.6), 0, Math.PI * 2);
        ctx.fill();
      }

      // Sparks
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.00004;
        s.life *= 0.965;
        if (s.life < 0.05) { sparks.splice(i, 1); continue; }
        const px = s.x * W;
        const py = s.y * H;
        const a = s.life * 0.95;
        ctx.fillStyle = `hsla(${s.hue}, 100%, 75%, ${a})`;
        ctx.beginPath();
        ctx.arc(px, py, 1.6 * dpr * s.life, 0, Math.PI * 2);
        ctx.fill();
        // streak
        ctx.strokeStyle = `hsla(${s.hue}, 100%, 80%, ${a * 0.6})`;
        ctx.lineWidth = 0.8 * dpr;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px - s.vx * 220, py - s.vy * 220);
        ctx.stroke();
      }

      ctx.globalCompositeOperation = "source-over";
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf.current); ro.disconnect(); };
  }, [analyser]);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" style={{ mixBlendMode: "screen" }} data-testid="hologram-dust" />;
}

/* ---------------- FLOOR LIGHT POOL ---------------- */
function FloorPool({ analyser, bpm = 108 }) {
  const ref = useRef(null);
  const raf = useRef(0);
  const bpmRef = useRef(bpm);
  useEffect(() => { bpmRef.current = bpm > 0 ? bpm : 108; }, [bpm]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const freq = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const start = performance.now();
    const loop = (now) => {
      const t = (now - start) / 1000;
      let bass = 0;
      if (analyser && freq) {
        analyser.getByteFrequencyData(freq);
        const n = freq.length;
        const bEnd = Math.floor(n * 0.12);
        let bs = 0;
        for (let i = 0; i < bEnd; i++) bs += freq[i];
        bass = bs / (bEnd || 1) / 255;
      } else {
        const liveBpm = Math.max(40, Math.min(220, bpmRef.current || 108));
        const beatLen = 60 / liveBpm;
        const beatPos = (t % beatLen) / beatLen;
        bass = Math.exp(-beatPos * 8) * 0.9 + 0.1;
      }
      const scale = 1 + bass * 0.18;
      const op = 0.55 + bass * 0.35;
      el.style.transform = `translateX(-50%) scaleX(${scale}) scaleY(${0.85 + bass * 0.1})`;
      el.style.opacity = String(op);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [analyser]);

  return (
    <div
      ref={ref}
      data-testid="hologram-floor-pool"
      className="absolute pointer-events-none"
      style={{
        left: "50%",
        bottom: "4%",
        width: "62%",
        height: "16%",
        transform: "translateX(-50%)",
        background:
          "radial-gradient(ellipse at center, rgba(120,230,255,0.85) 0%, rgba(0,180,255,0.45) 30%, rgba(255,60,160,0.18) 65%, rgba(0,0,0,0) 100%)",
        filter: "blur(14px)",
        mixBlendMode: "screen",
      }}
    />
  );
}

/* ---------------- HEX INTERFERENCE OVERLAY ---------------- */
function HexInterference() {
  return (
    <div
      data-testid="hologram-hex"
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "repeating-linear-gradient(60deg, rgba(120,230,255,0.05) 0 1px, transparent 1px 26px), repeating-linear-gradient(-60deg, rgba(120,230,255,0.05) 0 1px, transparent 1px 26px)",
        backgroundSize: "52px 52px",
        animation: "hexDrift 18s linear infinite",
        mixBlendMode: "screen",
        opacity: 0.32,
      }}
    />
  );
}

/* ---------------- HOLOGRAM FIGURE (the performer) ----------------
 * CSS-image-driven performer rendered as a SCI-FI HOLOGRAM, not a photo:
 *   - black-background wrapper + screen-blended figure layers → translucent
 *   - strong cyan tint via SVG color-matrix → looks "transmitted"
 *   - dense 2-px scanlines drift downward
 *   - random glitch displacements (CSS clip-path bands shift horizontally)
 *   - vertical light-beam emitter at the feet (the projector)
 *   - base disc / pad with cyan glow + radiating rings
 *   - flicker pulse on the alpha so it reads as "unstable signal"
 *   - chromatic aberration via cyan + pink offset ghosts
 * Pure CSS. Zero AI credits.
 *
 * Exported so the WatchModal hero (and other surfaces) can drop the same
 * dancing performer in by passing a `src` and the active track's `bpm`.
 */
function ensureHologramSvgFilters() {
  if (typeof document === "undefined") return;
  if (document.getElementById("hologram-svg-filters")) return;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.id = "hologram-svg-filters";
  svg.setAttribute("style", "position:absolute;width:0;height:0;pointer-events:none");
  svg.setAttribute("aria-hidden", "true");
  // Color-matrix that pushes the figure toward cyan/electric-blue while
  // preserving the highlights — gives the "transmitted hologram" look.
  svg.innerHTML = `
    <defs>
      <filter id="hologram-tint">
        <feColorMatrix type="matrix" values="
          0.45 0.30 0.25 0  0
          0.20 0.85 0.55 0  0
          0.30 0.65 1.05 0  0
          0    0    0    1  0" />
        <feComponentTransfer>
          <feFuncR type="gamma" amplitude="1.05" exponent="0.9" />
          <feFuncG type="gamma" amplitude="1.10" exponent="0.85" />
          <feFuncB type="gamma" amplitude="1.20" exponent="0.85" />
        </feComponentTransfer>
      </filter>
    </defs>`;
  document.body.appendChild(svg);
}

export function HologramFigure({ src, analyser = null, bpm = 108, testId = "hologram-figure" }) {
  const wrapRef = useRef(null);
  // No more JS rAF transform loop — that was creating the "jumping" stutter
  // by fighting CSS transitions. Instead we drive the dance with a pure CSS
  // keyframe animation whose duration is derived from the BPM. GPU-accelerated,
  // smooth, glitch-free.
  useEffect(() => { ensureHologramSvgFilters(); }, []);
  // analyser/glitch refs preserved for prop-API compatibility (ignored here)
  // eslint-disable-next-line no-unused-vars
  const _a = analyser;

  if (!src) return null;

  // ---- Background-crowd ELIMINATION ----
  // The AI hologram images bake in a stage crowd / pyrotechnics around the
  // performer. We stack TWO masks combined with mask-composite: intersect:
  //   1. Tight vertical-column ellipse around just the performer's silhouette
  //   2. Hard linear cut-off at the bottom that removes the crowd at the feet
  // Anything outside both masks becomes transparent — the crowd disappears.
  const figureMask =
    "radial-gradient(ellipse 22% 46% at 50% 36%, rgba(0,0,0,1) 72%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%), " +
    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 55%, rgba(0,0,0,0) 68%)";
  const figureMaskComposite = "intersect";

  // Reliable explicit centering — flex-positioning absolute children is
  // browser-dependent. We instead center each image with transform.
  const centeredImg = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    height: "92%",
    maxWidth: "70%",
    objectFit: "contain",
    objectPosition: "center top",
  };

  // GENTLE constant breath cycle — independent of BPM. Fast BPMs (168+)
  // were making the cycle 0.7s which read as "jumping". A holographic
  // performer should look ALIVE (subtle breath / sway), not actively dancing.
  // Real "dance" comes from the music, not the figure.
  const danceDuration = 6; // seconds per cycle, fixed

  return (
    <div
      ref={wrapRef}
      data-testid={testId}
      className="absolute inset-0 pointer-events-none holo-dance"
      style={{
        transformOrigin: "50% 100%",
        willChange: "transform",
        animationDuration: `${danceDuration}s`,
      }}
    >
      {/* ---- BASE PROJECTOR EMITTER (the hologram pad/disc) ---- */}
      {/* Glowing oval pad at the performer's feet */}
      <div
        aria-hidden="true"
        className="absolute"
        data-testid="hologram-emitter-pad"
        style={{
          left: "50%",
          bottom: "12%",
          width: "30%",
          height: "5%",
          transform: "translateX(-50%)",
          background:
            "radial-gradient(ellipse at center, rgba(160,255,255,0.95) 0%, rgba(60,200,255,0.7) 30%, rgba(255,80,170,0.35) 65%, rgba(0,0,0,0) 100%)",
          filter: "blur(2px)",
          mixBlendMode: "screen",
          animation: "emitterPulse 2.4s ease-in-out infinite",
        }}
      />
      {/* Vertical light cone from the pad upward — the projection beam */}
      <div
        aria-hidden="true"
        className="absolute"
        style={{
          left: "50%",
          bottom: "12%",
          width: "32%",
          height: "60%",
          transform: "translateX(-50%)",
          background:
            "linear-gradient(to top, rgba(120,230,255,0.55) 0%, rgba(120,230,255,0.18) 35%, rgba(120,230,255,0.04) 75%, rgba(0,0,0,0) 100%)",
          mixBlendMode: "screen",
          maskImage:
            "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0.2) 80%, rgba(0,0,0,0) 100%), radial-gradient(ellipse 60% 100% at 50% 100%, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
          WebkitMaskImage:
            "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0.2) 80%, rgba(0,0,0,0) 100%), radial-gradient(ellipse 60% 100% at 50% 100%, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
          filter: "blur(2px)",
        }}
      />

      {/* ---- THE HOLOGRAM FIGURE — clear artist + subtle hologram cues ---- */}
      <div
        className="absolute inset-0 holo-flicker"
        style={{
          willChange: "opacity",
          filter:
            "drop-shadow(0 0 14px rgba(120,230,255,0.6)) drop-shadow(0 0 40px rgba(80,180,255,0.35))",
        }}
      >
        <div
          className="absolute inset-0"
        >
          {/* Pink-shifted offset layer (chromatic aberration ghost) */}
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              ...centeredImg,
              transform: "translate(calc(-50% + 3px), -50%)",
              maskImage: figureMask,
              WebkitMaskImage: figureMask,
              maskComposite: figureMaskComposite,
              WebkitMaskComposite: "source-in",
              filter: "saturate(1.4) contrast(1.15) brightness(1.05) hue-rotate(15deg)",
              opacity: 0.4,
              mixBlendMode: "screen",
            }}
          />
          {/* Cyan-shifted offset layer */}
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              ...centeredImg,
              transform: "translate(calc(-50% - 3px), -50%)",
              maskImage: figureMask,
              WebkitMaskImage: figureMask,
              maskComposite: figureMaskComposite,
              WebkitMaskComposite: "source-in",
              filter: "saturate(1.4) contrast(1.15) brightness(1.05) hue-rotate(-10deg)",
              opacity: 0.4,
              mixBlendMode: "screen",
            }}
          />
          {/* MAIN figure — clear, opaque, recognisable artist */}
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              ...centeredImg,
              maskImage: figureMask,
              WebkitMaskImage: figureMask,
              maskComposite: figureMaskComposite,
              WebkitMaskComposite: "source-in",
              filter: "saturate(1.35) contrast(1.18) brightness(1.1)",
            }}
          />
          {/* Subtle scanlines (every 4 px) — hologram cue without obscuring face */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 3px, rgba(0,0,0,0.30) 3px 4px)",
              maskImage: figureMask,
              WebkitMaskImage: figureMask,
              maskComposite: figureMaskComposite,
              WebkitMaskComposite: "source-in",
              mixBlendMode: "multiply",
              opacity: 0.45,
              animation: "scanShift 4s linear infinite",
            }}
          />
          {/* Shimmer wave sweeping down the body */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(120,230,255,0) 0%, rgba(120,230,255,0) 40%, rgba(180,250,255,0.55) 47%, rgba(255,90,180,0.4) 50%, rgba(120,230,255,0) 56%, rgba(120,230,255,0) 100%)",
              maskImage: figureMask,
              WebkitMaskImage: figureMask,
              maskComposite: figureMaskComposite,
              WebkitMaskComposite: "source-in",
              mixBlendMode: "screen",
              animation: "shimmerSweep 5s linear infinite",
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ---------------- MAIN STAGE ---------------- */
export default function HologramStage({
  src,
  analyser = null,
  bpm = 108,
  className = "",
  testId = "hologram-stage",
}) {
  return (
    <div
      data-testid={testId}
      className={`absolute inset-0 overflow-hidden ${className}`}
      style={{
        background:
          "radial-gradient(ellipse at 50% 60%, rgba(10,30,55,0.85) 0%, rgba(2,4,12,1) 70%)",
      }}
    >
      {/* 1. Volumetric god-rays + halo behind */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <GodRays analyser={analyser} bpm={bpm} />
      </div>

      {/* 2. Floor light pool */}
      <FloorPool analyser={analyser} bpm={bpm} />

      {/* 3. Mirrored floor reflection (lightweight CSS image, no second WebGL context) */}
      <div
        className="absolute pointer-events-none"
        data-testid="hologram-reflection"
        style={{
          left: 0, right: 0,
          bottom: "-22%",
          height: "55%",
          transform: "scaleY(-1)",
          opacity: 0.28,
          filter: "blur(8px) saturate(1.45) brightness(0.85) hue-rotate(8deg)",
          maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0) 75%)",
          WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0) 75%)",
          mixBlendMode: "screen",
          backgroundImage: src ? `url(${src})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* 4. Main performer — CSS-image-driven, always visible, animated by audio. */}
      <div
        className="absolute inset-0 z-10"
        style={{
          filter:
            "drop-shadow(0 0 22px rgba(120,230,255,0.55))",
        }}
      >
        <HologramFigure src={src} analyser={analyser} bpm={bpm} />
      </div>

      {/* 5. Hex interference grid */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <HexInterference />
      </div>

      {/* 6. Foreground dust + sparks */}
      <div className="absolute inset-0 z-30 pointer-events-none">
        <DustField analyser={analyser} bpm={bpm} />
      </div>

      {/* 7. Subtle atmospheric haze (only at top + very bottom edges) */}
      <div
        className="absolute inset-0 z-40 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(0,10,25,0.45) 0%, rgba(0,10,25,0.0) 18%, rgba(0,10,25,0.0) 80%, rgba(0,10,25,0.35) 100%)",
        }}
      />

      <style>{`
        @keyframes hexDrift {
          from { background-position: 0 0, 0 0; }
          to   { background-position: 44px 44px, -44px 44px; }
        }
        @keyframes scanShift {
          from { background-position: 0 0; }
          to   { background-position: 0 8px; }
        }
        @keyframes shimmerSweep {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </div>
  );
}
